import type { AnySceneObjectT } from "tweenly"
/**
 * Animation compatibility helper
 * Determines if a tween preset is safe for a given object based on
 * its position mode (absolute vs static/flex child) and type (svg).
 */

export type AnimationCompatibility = "safe" | "visual-difference" | "problematic"

type CompatibilityResult = {
    level: AnimationCompatibility
    message: string | null
}

const FLEX_VISUAL_DIFFERENCE_TWEENS = new Set([
    "zoomIn",
    "zoomOut",
    "slideFromLeft",
    "slideToLeft",
    "slideFromRight",
    "slideToRight",
    "slideFromTop",
    "slideToTop",
    "slideFromBottom",
    "slideToBottom",
])

const FLEX_PROBLEMATIC_TWEENS = new Set(["sizeIn", "sizeOut"])

const SVG_VISUAL_DIFFERENCE_TWEENS = new Set(["sizeIn", "sizeOut"])

const isFlexChild = (object: AnySceneObjectT): boolean =>
    object.styles.some((s) => s.property === "position" && s.value === "static")

export const getAnimationCompatibility = (
    object: AnySceneObjectT | undefined,
    tweenName: string
): CompatibilityResult => {
    if (!object) return { level: "safe", message: null }

    // Flex child checks (position: static)
    if (isFlexChild(object)) {
        if (FLEX_PROBLEMATIC_TWEENS.has(tweenName)) {
            return {
                level: "problematic",
                message:
                    "Flex child: changes element size and rearranges flex layout during animation",
            }
        }
        if (FLEX_VISUAL_DIFFERENCE_TWEENS.has(tweenName)) {
            return {
                level: "visual-difference",
                message: "Flex child: element's space in layout is preserved during animation",
            }
        }
    }

    // SVG checks
    if (object.type === "svg" && SVG_VISUAL_DIFFERENCE_TWEENS.has(tweenName)) {
        return {
            level: "visual-difference",
            message: "SVG: resizing may deform the viewBox",
        }
    }

    return { level: "safe", message: null }
}
